import React, { useState } from 'react';
import axios from 'axios';

const MFASetup = ({ token, onComplete, onClose }) => {
  const [step, setStep] = useState(1); // 1: password, 2: qr, 3: verify, 4: backup
  const [password, setPassword] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const baseURL = process.env.REACT_APP_API_BASE_URL || '';

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${baseURL}/api/users/mfa/setup`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setBackupCodes(data.backup_codes || []);
      setStep(2);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    // Use the shared verification function with current state
    await verifySetupWithCode(verificationCode);
  };

  const copyText = (txt, buttonElement) => {
    navigator.clipboard.writeText(txt).then(() => {
      // Show success feedback
      const originalText = buttonElement.textContent;
      buttonElement.textContent = 'Copied!';
      buttonElement.classList.add('bg-green-100', 'text-green-700');
      setTimeout(() => {
        buttonElement.textContent = originalText;
        buttonElement.classList.remove('bg-green-100', 'text-green-700');
      }, 2000);
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  const handleVerificationCodeChange = (e) => {
    const code = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(code);
    
    // Auto-submit when 6 digits are entered
    if (code.length === 6 && !loading) {
      setTimeout(() => verifySetupWithCode(code), 500);
    }
  };

  const verifySetupWithCode = async (code) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${baseURL}/api/users/mfa/verify`,
        { code },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.token) {
        localStorage.setItem('token', data.token);
      }
      setStep(4);
      onComplete && onComplete();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center space-x-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= i ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {step > i ? '✓' : i}
            </div>
            {i < 4 && (
              <div className={`w-12 h-1 mx-2 ${step > i ? 'bg-blue-600' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Password Verification */}
      {step === 1 && (
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">Enable Two-Factor Authentication</h3>
          <p className="text-sm text-gray-600 mb-4">Enter your current password to proceed with MFA setup.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Current Password"
            className="w-full p-3 border rounded-lg mb-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && !loading && password && startSetup()}
          />
          {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              type="button"
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700"
            >
              Cancel
            </button>
            <button
              disabled={!password || loading}
              onClick={startSetup}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: QR Code Display */}
      {step === 2 && (
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">Scan QR Code</h3>
          <p className="text-sm text-gray-600 mb-4">
            Scan this QR code with Google Authenticator, Microsoft Authenticator, or any compatible TOTP app.
          </p>
          <div className="flex justify-center mb-4">
            {qrCode ? (
              <div className="p-4 bg-white border-2 border-gray-200 rounded-lg shadow-sm">
                <img
                  src={qrCode}
                  alt="MFA QR Code"
                  className="w-64 h-64 object-contain"
                />
              </div>
            ) : (
              <div className="w-64 h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-gray-500">Loading QR Code...</div>
              </div>
            )}
          </div>
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="font-medium mb-2 text-sm">Can't scan? Enter this code manually:</p>
            <div className="flex items-center justify-between bg-white border rounded p-3">
              <code className="text-xs break-all mr-3 font-mono">{secret}</code>
              <button
                type="button"
                onClick={(e) => copyText(secret, e.target)}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                Copy
              </button>
            </div>
          </div>
          {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              I've Added the Account
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Code Verification */}
      {step === 3 && (
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">Verify Setup</h3>
          <p className="text-sm text-gray-600 mb-4">Enter the 6-digit code from your authenticator app to complete setup.</p>
          <input
            value={verificationCode}
            onChange={handleVerificationCodeChange}
            maxLength={6}
            className="w-full p-4 border rounded-lg tracking-widest text-center text-2xl mb-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
            placeholder="123456"
            autoFocus
          />
          <p className="text-xs text-gray-500 mb-4">Code will auto-submit when 6 digits are entered</p>
          {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-700"
            >
              Back
            </button>
            <button
              disabled={verificationCode.length !== 6 || loading}
              onClick={verifySetup}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Backup Codes */}
      {step === 4 && (
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2 text-green-600">🎉 MFA Enabled Successfully!</h3>
          <p className="text-sm text-gray-600 mb-4">
            Save these backup codes in a secure place. Each code can only be used once if you lose access to your authenticator app.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm font-medium text-yellow-800 mb-3">⚠️ Important: Save these backup codes now!</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {backupCodes.map((code, i) => (
                <code
                  key={i}
                  className="bg-white border px-3 py-2 rounded text-sm text-center font-mono select-all"
                >
                  {code}
                </code>
              ))}
            </div>
            <div className="flex justify-center space-x-2">
              <button
                onClick={(e) => copyText(backupCodes.join('\n'), e.target)}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors"
              >
                Copy All Codes
              </button>
              <button
                onClick={() => {
                  const element = document.createElement('a');
                  const file = new Blob([`MFA Backup Codes\n\n${backupCodes.join('\n')}\n\nGenerated: ${new Date().toLocaleString()}`], {type: 'text/plain'});
                  element.href = URL.createObjectURL(file);
                  element.download = 'mfa-backup-codes.txt';
                  document.body.appendChild(element);
                  element.click();
                  document.body.removeChild(element);
                }}
                className="px-4 py-2 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 transition-colors"
              >
                Download
              </button>
            </div>
          </div>
          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Complete Setup
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MFASetup;